import { store } from '@/store';
import { incrementPartNumber } from '@/store/slices/recordingSlice';
import { aiScribeService } from './ai-scribe-service';
import { AudioProcessorService } from './audio-processor';
import { retryQueueService } from './retry-queue';

export class UploadScheduler {
  private audioProcessor: AudioProcessorService;
  private isRunning = false;
  private timer: NodeJS.Timeout | null = null;
  private uploadInterval: number;
  private silenceDurationThreshold: number;
  private createdBy: string;

  constructor(audioProcessor: AudioProcessorService, createdBy: string) {
    this.audioProcessor = audioProcessor;
    this.createdBy = createdBy;
    const { uploadInterval, silenceDurationThreshold } =
      store.getState().recording.config;
    this.uploadInterval = uploadInterval * 1000;
    this.silenceDurationThreshold = silenceDurationThreshold;
  }

  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.scheduleNextCheck();
  }

  stop() {
    this.isRunning = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private scheduleNextCheck() {
    if (!this.isRunning) return;
    this.timer = setTimeout(() => {
      this.checkForSilenceAndUpload();
    }, this.uploadInterval);
  }

  private async checkForSilenceAndUpload() {
    if (!this.isRunning) return;

    await this.audioProcessor.waitForSilence(this.silenceDurationThreshold);
    if (!this.isRunning) return;

    try {
      const chunkResult = await this.audioProcessor.createChunk();
      if (chunkResult.blob.size > 0) {
        this.uploadChunk(chunkResult.blob, chunkResult.durationSeconds);
      }
    } catch (error) {
      console.error('[UploadScheduler] Error creating chunk:', error);
    }

    this.scheduleNextCheck();
  }

  private uploadChunk(
    blob: Blob,
    durationSeconds: number,
    isFinalPart: boolean = false,
    attempt: number = 0
  ) {
    const state = store.getState();
    const { currentPartNumber } = state.recording;
    const selectedAppointment = state.data?.appointments?.selectedAppointment;
    const scribeJobId = selectedAppointment?.scribeJobId;

    if (!scribeJobId) {
      // Wait briefly for scribeJobId to be set by createAiScribeJob response
      const maxAttempts = 30; // ~15s total at 500ms
      if (attempt < maxAttempts) {
        console.warn(
          `[UploadScheduler] scribeJobId missing. Will retry upload for part ${currentPartNumber} (attempt ${
            attempt + 1
          }/${maxAttempts})`
        );
        setTimeout(
          () =>
            this.uploadChunk(blob, durationSeconds, isFinalPart, attempt + 1),
          500
        );
        return;
      }
      console.error(
        '[UploadScheduler] Missing scribeJobId for upload after retries; dropping chunk'
      );
      return;
    }

    store.dispatch(incrementPartNumber());

    this.uploadWavChunk(
      blob,
      currentPartNumber,
      this.createdBy,
      scribeJobId,
      isFinalPart,
      durationSeconds
    ).catch((error) => {
      console.error(
        `[UploadScheduler] Upload failed for part ${currentPartNumber}:`,
        error
      );
    });
  }

  async uploadWavChunk(
    blob: Blob,
    partNumber: number,
    createdBy: string,
    scribeJobId: string,
    isFinalPart: boolean = false,
    durationSeconds: number
  ): Promise<{ partId?: string; partNumber?: number }> {
    // Get the required IDs from store for filename (server expects this format)
    const state = store.getState();
    const {
      appointmentId: recordingAppointmentId,
      patientId: recordingPatientId,
      locationId: recordingLocationId,
    } = state.recording;
    const selectedAppointment = state.data?.appointments?.selectedAppointment;

    // Use IDs from recording state, or fall back to selected appointment if missing
    const appointmentId =
      recordingAppointmentId || selectedAppointment?.appointmentId || null;
    const patientId =
      recordingPatientId || selectedAppointment?.patientId || null;
    const locationId =
      recordingLocationId ||
      selectedAppointment?.locationId ||
      state.data.selectedLocation.locationId ||
      null;

    // Validate that we have all required IDs for the filename
    if (!appointmentId || !patientId || !locationId) {
      throw new Error(
        `Missing required IDs for filename: appointmentId=${appointmentId}, patientId=${patientId}, locationId=${locationId}`
      );
    }

    // Round durationSeconds to nearest integer as required by the API
    const roundedDurationSeconds = Math.round(durationSeconds);

    console.warn(
      `[UploadScheduler] 📤 Preparing to upload chunk:
       - Part Number: ${partNumber}
       - Blob size: ${blob.size.toLocaleString()} bytes (${(
        blob.size / 1024
      ).toFixed(2)} KB)
       - Original duration: ${durationSeconds.toFixed(3)}s
       - Rounded duration: ${roundedDurationSeconds}s  ← THIS VALUE GOES TO API
       - Is Final Part: ${isFinalPart}
       - Scribe Job ID: ${scribeJobId}`
    );

    const timestampInMilliseconds = Date.now();
    const audioFile = new File(
      [blob],
      `${locationId}-${patientId}-${appointmentId}-${timestampInMilliseconds}.wav`,
      { type: 'audio/wav' }
    );

    try {
      const response = await aiScribeService.uploadAudioChunk({
        partNumber,
        createdBy,
        audioFile,
        scribeJobId,
        isFinalPart,
        durationSeconds: roundedDurationSeconds,
      });

      console.log(
        `[UploadScheduler] ✅ Upload successful for part ${partNumber} (duration: ${roundedDurationSeconds}s)`
      );

      if (!response.success) {
        throw new Error(response.error || 'Upload failed');
      }

      // Return the partId and partNumber from the response
      return {
        partId: response.data?.partId,
        partNumber: response.data?.partNumber,
      };
    } catch (error) {
      console.error(`❌ Upload failed for part ${partNumber}:`, error);

      retryQueueService.addToQueue({
        blob,
        partNumber,
        createdBy,
        scribeJobId,
        isFinalPart,
        durationSeconds,
        maxRetries: 3,
        uploadFunction: async (item) => {
          await this.uploadWavChunk(
            item.blob,
            item.partNumber,
            item.createdBy,
            item.scribeJobId,
            item.isFinalPart,
            item.durationSeconds
          );
        },
      });

      throw error;
    }
  }

  /**
   * Upload WAV chunk for section-based recording to generate section JSON
   * This uses the new generate-section-json endpoint
   */
  async uploadSectionWavChunk(
    blob: Blob,
    partNumber: number,
    createdBy: string,
    scribeJobId: string,
    sectionTag: string,
    isFinalPart: boolean = false,
    durationSeconds: number,
    dxList?: any
  ): Promise<{ partId?: string; partNumber?: number }> {
    // Get the required IDs from store for filename (server expects this format)
    const state = store.getState();
    const {
      appointmentId: recordingAppointmentId,
      patientId: recordingPatientId,
      locationId: recordingLocationId,
    } = state.recording;
    const selectedAppointment = state.data?.appointments?.selectedAppointment;

    // Use IDs from recording state, or fall back to selected appointment if missing
    const appointmentId =
      recordingAppointmentId || selectedAppointment?.appointmentId || null;
    const patientId =
      recordingPatientId || selectedAppointment?.patientId || null;
    const locationId =
      recordingLocationId ||
      selectedAppointment?.locationId ||
      state.data.selectedLocation.locationId ||
      null;

    // Validate that we have all required IDs for the filename
    if (!appointmentId || !patientId || !locationId) {
      throw new Error(
        `Missing required IDs for filename: appointmentId=${appointmentId}, patientId=${patientId}, locationId=${locationId}`
      );
    }

    // Round durationSeconds to nearest integer as required by the API
    const roundedDurationSeconds = Math.round(durationSeconds);

    const timestampInMilliseconds = Date.now();
    const audioFile = new File(
      [blob],
      `${locationId}-${patientId}-${appointmentId}-${timestampInMilliseconds}.wav`,
      { type: 'audio/wav' }
    );

    try {
      const response = await aiScribeService.uploadSectionAudioChunk({
        partNumber,
        createdBy,
        audioFile,
        scribeJobId,
        sectionTag,
        isFinalPart,
        durationSeconds: roundedDurationSeconds,
        dxList,
      });

      if (!response.success) {
        throw new Error(response.error || 'Section upload failed');
      }

      // Return the partId and partNumber from the response
      return {
        partId: response.data?.partId,
        partNumber: response.data?.partNumber,
      };
    } catch (error) {
      console.error(`❌ Section upload failed for part ${partNumber}:`, error);

      retryQueueService.addToQueue({
        blob,
        partNumber,
        createdBy,
        scribeJobId,
        isFinalPart,
        durationSeconds,
        dxList,
        maxRetries: 3,
        uploadFunction: async (item) => {
          await this.uploadSectionWavChunk(
            item.blob,
            item.partNumber,
            item.createdBy,
            item.scribeJobId,
            sectionTag,
            item.isFinalPart,
            item.durationSeconds,
            item.dxList
          );
        },
      });

      throw error;
    }
  }
}
